/**
 * Campaign display helpers (SILL-396 / plan 61 M6).
 *
 * Pure, unit-tested mappers for the campaign list/history UI: status-chip label
 * + Tailwind class string, an auto-name fallback, and a relative schedule line.
 *
 * ADR-034: status chip classes are STATIC literal strings keyed by status — not
 * dynamically-interpolated atoms — so Tailwind statically detects every class
 * and the styling-emission guard has nothing to drop. We deliberately avoid a
 * `campaign-chip-${status}` @layer-components atom here.
 */
import type { Campaign, CampaignStatus } from "./api";
import { cohortLabel } from "./audience";

export interface StatusChip {
  label: string;
  /** Static Tailwind classes for the chip — color-coded by lifecycle. */
  className: string;
}

/**
 * Status → human label + chip color. Color intent (each tone is distinct — no
 * two statuses share a hue):
 *   draft     — neutral gray (not yet committed)
 *   scheduled — primary green-teal (queued, reversible)
 *   sending   — info blue (in-progress, transient — NOT a warning)
 *   sent      — success green (terminal, all delivered)
 *   partial   — warning amber (terminal, some delivered + some failed)
 *   failed    — destructive red (terminal, nothing delivered)
 *   canceled  — muted destructive (terminal, stopped by the pro)
 */
const STATUS_CHIPS: Record<CampaignStatus, StatusChip> = {
  draft: {
    label: "Draft",
    className:
      "bg-muted text-fg-muted border border-border",
  },
  scheduled: {
    label: "Scheduled",
    className:
      "bg-primary-soft text-primary-dark border border-[color-mix(in_oklch,var(--color-primary)_30%,transparent)]",
  },
  sending: {
    label: "Sending",
    className:
      "bg-[color-mix(in_oklch,var(--color-info)_15%,transparent)] text-[color:var(--color-info)] border border-[color-mix(in_oklch,var(--color-info)_40%,transparent)]",
  },
  sent: {
    label: "Sent",
    className:
      "bg-[color-mix(in_oklch,var(--color-success)_15%,transparent)] text-[color:var(--color-success)] border border-[color-mix(in_oklch,var(--color-success)_40%,transparent)]",
  },
  partial: {
    label: "Partial",
    className:
      "bg-[color-mix(in_oklch,var(--color-warning)_15%,transparent)] text-[color:var(--color-warning)] border border-[color-mix(in_oklch,var(--color-warning)_40%,transparent)]",
  },
  failed: {
    label: "Failed",
    className:
      "bg-[color-mix(in_oklch,var(--color-destructive)_12%,transparent)] text-[color:var(--color-destructive)] border border-[color-mix(in_oklch,var(--color-destructive)_35%,transparent)]",
  },
  canceled: {
    label: "Canceled",
    className:
      "bg-[color-mix(in_oklch,var(--color-destructive)_10%,transparent)] text-[color:var(--color-destructive)] border border-[color-mix(in_oklch,var(--color-destructive)_30%,transparent)]",
  },
};

export function statusChip(status: CampaignStatus): StatusChip {
  return STATUS_CHIPS[status] ?? STATUS_CHIPS.draft;
}

// ── Status-set predicates ─────────────────────────────────────────────────────
// The taxonomy of "which statuses cluster together" lives here, not open-coded at
// each call site, so adding a future terminal status touches one file.

/**
 * A campaign has a per-recipient send log — and belongs in the "sent" list group
 * — once it has left draft/scheduled (i.e. a send was started).
 */
export function hasSendLog(status: CampaignStatus): boolean {
  return status !== "draft" && status !== "scheduled";
}

/** Terminal states that delivered to at least some recipients (show Delivered). */
export function isDelivered(status: CampaignStatus): boolean {
  return status === "sent" || status === "partial";
}

/** Terminal send outcomes — fully delivered, degraded, or a total failure. */
export function isTerminalSend(status: CampaignStatus): boolean {
  return status === "sent" || status === "partial" || status === "failed";
}

/**
 * The campaign's display title — the heading shown on every campaign card and
 * the viewer header. It is the pro's own name when they set one, otherwise the
 * subject·date auto-name. The backend persists an EMPTY name for an unnamed
 * campaign (it no longer auto-fills the row), so a blank name here reliably
 * means "never named" → render the auto-name fallback.
 */
export function campaignTitle(c: Campaign): string {
  return c.name.trim() || campaignAutoName(c);
}

/**
 * A short secondary line for a campaign row: scheduled time, sent count, or the
 * created date — whichever is most informative for the status.
 */
export function campaignMetaLine(c: Campaign): string {
  if (c.status === "scheduled" && c.scheduled_for) {
    return `Scheduled for ${formatScheduleStamp(c.scheduled_for)}`;
  }
  if (c.status === "sent") {
    const n = c.sent_count;
    return `Sent to ${n} ${n === 1 ? "recipient" : "recipients"}`;
  }
  if (c.status === "partial") {
    // Completed with a mix — surface both counts so the failure is visible on the
    // minimized card without opening the campaign.
    return `${c.sent_count} sent · ${c.failed_count} failed`;
  }
  if (c.status === "failed") {
    // Nothing delivered — every recipient failed.
    const n = c.failed_count;
    return `All ${n} ${n === 1 ? "recipient" : "recipients"} failed`;
  }
  if (c.status === "sending") {
    return "Delivery in progress…";
  }
  if (c.status === "canceled") {
    return "Canceled";
  }
  const d = new Date(c.updated_at || c.created_at);
  if (!Number.isNaN(d.getTime())) {
    return `Last edited ${d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })}`;
  }
  return "Draft";
}

/** Human-readable absolute schedule stamp, e.g. "Jul 1, 5:00 PM". */
export function formatScheduleStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Month + day only, e.g. "Jun 12" (no year) — the design's sent-row stamp. */
export function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Day · time, e.g. "Jun 14 · 9:00 AM" — the design's scheduled-row stamp. */
export function formatDayTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time}`;
}

/**
 * The cohort the campaign targets. For an explicit hand-selected campaign this
 * is the count of chosen recipients ("3 selected"); otherwise it is derived
 * from the stored audience preset (e.g. "Going cold"). This is the honest
 * source — the backend's persisted `name` is fed an empty cohort label, so we
 * resolve it ourselves.
 */
export function campaignCohort(c: Campaign): string {
  if (c.audience?.explicit) {
    const n = c.audience.include?.length ?? 0;
    return `${n} selected`;
  }
  return cohortLabel(c.audience?.filter?.Preset);
}

/** Whether the campaign was sent to an explicit hand-selected recipient list. */
export function isExplicitAudience(c: Campaign): boolean {
  return Boolean(c.audience?.explicit);
}

/**
 * Per-recipient delivery-status chip for the campaign recipient list — mirrors
 * statusChip's token idiom (success / warning / destructive color-mix). All
 * class names are static literals so Tailwind emits them (ADR-034). Unknown
 * statuses fall back to a neutral chip.
 */
export interface DeliveryChip {
  label: string;
  className: string;
}

const DELIVERY_CHIPS: Record<string, DeliveryChip> = {
  sent: {
    label: "Delivered",
    className:
      "bg-[color-mix(in_oklch,var(--color-success)_15%,transparent)] text-[color:var(--color-success)]",
  },
  sending: {
    label: "Sending",
    className:
      "bg-[color-mix(in_oklch,var(--color-warning)_15%,transparent)] text-[color:var(--color-warning)]",
  },
  failed: {
    label: "Failed",
    className:
      "bg-[color-mix(in_oklch,var(--color-destructive)_12%,transparent)] text-[color:var(--color-destructive)]",
  },
  suppressed: {
    label: "Opted out",
    className: "bg-muted text-fg-muted",
  },
};

const DELIVERY_CHIP_NEUTRAL: DeliveryChip = {
  label: "Pending",
  className: "bg-muted text-fg-muted",
};

export function deliveryStatusChip(status: string): DeliveryChip {
  return DELIVERY_CHIPS[status] ?? { ...DELIVERY_CHIP_NEUTRAL, label: status || "Pending" };
}

/**
 * The auto-name fallback for a campaign the pro never named: the email subject
 * (the "templated content") plus the creation day, e.g.
 * "Open house Saturday · Jun 10". An empty subject falls back to "Campaign".
 * The date keeps unnamed campaigns uniquely identifiable in the history list.
 *
 * This mirrors the backend `campaigns.AutoName` label (subject · date) so a
 * campaign reads the same in the list, the viewer, and its materialized
 * template. The created day (not the send/schedule day) is used so a campaign's
 * identity stays stable across its lifecycle.
 */
export function campaignAutoName(c: Campaign): string {
  const subject =
    c.custom_subject.trim() || c.effective_subject?.trim() || "Campaign";
  return `${subject} · ${formatDay(c.created_at)}`;
}

/**
 * The campaign's THREE lifecycle groups for the history list. Scheduled is its
 * own section because — unlike a draft — a scheduled campaign is NOT editable in
 * place (clicking it opens the read-only viewer); it is only cancelable. Folding
 * it under a shared "Editable until they send" header was a lie.
 *   - scheduled: "Reversible until they send" — soonest fire first.
 *   - drafts:    "Editable — not sent yet"    — most-recently edited first.
 *   - sent:      sent + sending + canceled, "Read-only · duplicate to reuse" —
 *                most recent first.
 * Pure; the page passes the flat GET /campaigns list straight through.
 */
export interface CampaignGroups {
  scheduled: Campaign[];
  drafts: Campaign[];
  sent: Campaign[];
}

function recencyKey(c: Campaign): number {
  const t = new Date(c.updated_at || c.created_at).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function groupCampaigns(list: Campaign[]): CampaignGroups {
  const scheduled = list
    .filter((c) => c.status === "scheduled")
    .sort((a, b) => {
      const ta = a.scheduled_for ? new Date(a.scheduled_for).getTime() : 0;
      const tb = b.scheduled_for ? new Date(b.scheduled_for).getTime() : 0;
      return ta - tb; // soonest fire first
    });
  const drafts = list
    .filter((c) => c.status === "draft")
    .sort((a, b) => recencyKey(b) - recencyKey(a));
  const sent = list
    .filter((c) => hasSendLog(c.status))
    .sort((a, b) => recencyKey(b) - recencyKey(a));
  return { scheduled, drafts, sent };
}
